<script lang="ts" setup>
import Intro from '@/components/Intro.vue'
import LocationList from './components/LocationList.vue'
import { useLocationStore, useOptionStore } from '@/stores'
import useSeo from './seo'
import type { Meta } from '@/utils/types'

const route = useRoute()
const router = useRouter()
const locationStore = useLocationStore()
const optionStore = useOptionStore()
await locationStore.fetchLocations()
await locationStore.fetchLocationClasses()

const queryLocation = route.params.locationId
const citys = computed(() => optionStore.cityOption)
const city = computed(() => citys.value.find((o) => o.id === cityValue.value))
const types = computed(() => locationStore.locationClassesOption)
const type = computed(() => types.value.find((o) => o.id === typeValue.value))
const locations = computed(() => locationStore.locations)
const location = computed(() => locationStore.location)
// 地區
const cityValue = ref(route.query?.city as string || '')
// 類型
const typeValue = ref(route.query?.type as string || '')
const pageValue = ref(parseInt(route.query.page as string) || 1)
const pageMeta = computed(() => {
  return {
    last_page: 0,
    from: 0,
    per_page: 12,
    current_page: pageValue.value,
    to: 0,
    total: locations.value
      .filter((o) => cityValue.value === '' || o.city === city.value?.name)
      .filter((o) => typeValue.value === '' || o.class === type.value?.name)
      .length
  } as Meta
})

const filterLists = computed(() => {
  return locations.value
    .filter((o) => cityValue.value === '' || o.city === city.value?.name)
    .filter((o) => typeValue.value === '' || o.class === type.value?.name)
    .slice(pageValue.value > 1 ? (pageValue.value - 1) * pageMeta.value.per_page : 0, pageMeta.value.per_page * pageValue.value)
})

const filterString = computed(() =>
  objectToSearch({
    city: cityValue.value,
    type: typeValue.value,
    page: pageValue.value
  })
)
const searchCountText = computed(() => `hengstyle 恆隆行在 ${city.value?.name ?? '台灣'} 共有 ${pageMeta.value.total} 個 ${type.value?.name ?? '服務據點'}，歡迎立即用 Google 導航至恆隆行專櫃，或是用 Line 與專櫃聯絡。`)
const locationIndex = filterLists.value.findIndex((o) => o.id.toString() === queryLocation || o.seo.meta_slug === queryLocation)

// get location detail
if (locationIndex >= 0) {
  await locationStore.fetchLocation(queryLocation as string);
}

const handleChangePage = (page: number) => {
  pageValue.value = page
}

watch(
  () => filterString.value,
  () => {
    router.push(`/locations?${filterString.value}`)
    setTimeout(() => {
      const locationIntroDom = document.querySelector('.location-intro')
      if (locationIntroDom) {
        locationIntroDom.scrollIntoView({ behavior: "smooth" });
      }
    }, 300)
  }
)

useSeo(filterString.value, location.value)
</script>

<template>
  <div class="location-intro">
    <Intro subtitle="locations" :data.sync="{
      title: '百貨據點列表',
      text: searchCountText
    }" />
  </div>
  <div class="location-select">
    <el-select v-model="cityValue" class="h-select" placeholder="全部地區" @change="pageValue = 1">
      <el-option v-for="item in citys" :key="item.id" :label="item.name" :value="item.id" />
    </el-select>
    <el-select v-model="typeValue" class="h-select" placeholder="所有櫃位類型" @change="pageValue = 1">
      <el-option v-for="item in types" :key="item.id" :label="item.name" :value="item.id" />
    </el-select>
  </div>
  <div class="location-lists">
    <LocationList :lists.sync="filterLists" :current="locationIndex" :filter-query.sync="filterString" />
    <Pagination2 class="h-pagination center" layout="prev, pager, next" :page-meta="pageMeta"
      :handleChangePage="handleChangePage" />
  </div>
</template>

<style lang="scss" scoped>
.location-intro {
  max-width: toRem(550);
  margin-left: auto;
  margin-right: auto;
  padding-top: toRem(100);
  margin-bottom: toRem(150);

  @include max-media(1280) {
    padding-top: toRem(30);
    margin-bottom: toRem(50);
  }

  @include max-media(580) {
    padding-top: toRem(15);
  }

  :deep .intro {
    &__text {
      font-size: toRem(16);
      font-weight: 400;
    }
  }
}

.location-select {
  display: flex;
  justify-content: center;
  margin-bottom: toRem(36);

  @include max-media(480) {
    flex-direction: column;
    margin-bottom: toRem(20);
  }

  .el-select {
    margin-left: toRem(12);
    margin-right: toRem(12);

    @include max-media(480) {
      margin-left: 0;
      margin-right: 0;
      margin-bottom: toRem(20);
    }
  }
}
</style>
